/**
 * MuscleRadarChart - Radar chart showing muscle group volume distribution
 */

import { useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ResponsiveContainer,
  Tooltip
} from 'recharts';
import { Target } from 'lucide-react';

const MuscleRadarChart = ({ muscleGroups }) => {
  // Muscle group labels in Turkish
  const muscleLabels = {
    chest: 'Göğüs',
    back: 'Sırt',
    shoulders: 'Omuz',
    biceps: 'Biceps',
    triceps: 'Triceps',
    legs: 'Bacak',
    core: 'Karın',
    other: 'Diğer',
  };

  // Prepare radar data
  const radarData = useMemo(() => {
    // Main muscle groups to display (include core if it has data)
    let allMuscles = ['chest', 'back', 'shoulders', 'biceps', 'triceps', 'legs'];
    if (muscleGroups.core > 0) {
      allMuscles = ['chest', 'back', 'shoulders', 'biceps', 'triceps', 'legs', 'core'];
    }
    const maxVolume = Math.max(...Object.values(muscleGroups), 1);

    return allMuscles.map(muscle => ({
      muscle,
      label: muscleLabels[muscle],
      volume: muscleGroups[muscle] || 0,
      normalized: ((muscleGroups[muscle] || 0) / maxVolume) * 100,
      fullMark: 100,
    }));
  }, [muscleGroups]);

  // Calculate balance score
  const balanceScore = useMemo(() => {
    const volumes = radarData.map(d => d.volume);
    const nonZero = volumes.filter(v => v > 0);
    if (nonZero.length < 2) return null;

    const avg = nonZero.reduce((a, b) => a + b, 0) / nonZero.length;
    const variance = nonZero.reduce((sum, v) => sum + Math.pow(v - avg, 2), 0) / nonZero.length;
    const stdDev = Math.sqrt(variance);
    const cv = (stdDev / avg) * 100; // Coefficient of variation

    // Lower CV = more balanced (invert for score)
    const score = Math.max(0, Math.min(100, 100 - cv));
    return score.toFixed(0);
  }, [radarData]);

  // Find dominant and weak muscle groups
  const analysis = useMemo(() => {
    const sorted = [...radarData].sort((a, b) => b.volume - a.volume);
    const withVolume = sorted.filter(d => d.volume > 0);

    return {
      dominant: withVolume[0] || null,
      weak: withVolume.length > 1 ? withVolume[withVolume.length - 1] : null,
    };
  }, [radarData]);

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-3 shadow-xl">
          <p className="text-white font-medium">{data.label}</p>
          <p className="text-orange-400">
            {data.volume} set
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 }}
      className="bg-gray-900 border border-gray-800 rounded-xl p-4"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Target className="w-5 h-5 text-orange-500" />
          <h3 className="font-semibold text-white">Kas Grubu Dağılımı</h3>
        </div>
        {balanceScore && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400">Denge Skoru:</span>
            <span className={`text-sm font-bold ${
              Number(balanceScore) > 70 ? 'text-green-400' :
              Number(balanceScore) > 40 ? 'text-yellow-400' : 'text-red-400'
            }`}>
              %{balanceScore}
            </span>
          </div>
        )}
      </div>

      {/* Chart */}
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <RadarChart data={radarData} cx="50%" cy="50%" outerRadius="70%">
            <PolarGrid stroke="#374151" />
            <PolarAngleAxis
              dataKey="label"
              tick={{ fill: '#9CA3AF', fontSize: 12 }}
            />
            <PolarRadiusAxis
              angle={30}
              domain={[0, 100]}
              tick={false}
              axisLine={false}
            />
            <Radar
              name="Hacim"
              dataKey="normalized"
              stroke="#f97316"
              fill="#f97316"
              fillOpacity={0.3}
              strokeWidth={2}
            />
            <Tooltip content={<CustomTooltip />} />
          </RadarChart>
        </ResponsiveContainer>
      </div>

      {/* Analysis */}
      <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-800 text-sm">
        {analysis.dominant && (
          <div>
            <span className="text-gray-400">En çok: </span>
            <span className="text-white font-medium">{analysis.dominant.label}</span>
          </div>
        )}
        {analysis.weak && analysis.weak !== analysis.dominant && (
          <div>
            <span className="text-gray-400">Geliştirilmeli: </span>
            <span className="text-yellow-400 font-medium">{analysis.weak.label}</span>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default MuscleRadarChart;
